/**
 * IFRAP-AI - Widget Embeddable
 * Page légère affichant les KPIs en temps réel, conçue pour être intégrée
 * en iframe sur ifrap.org ou tout autre site.
 * 
 * URL: /embed/kpis
 * Paramètres URL:
 *   ?theme=dark|light (défaut: light)
 *   ?cols=1|2|3|4 (défaut: 2)
 *   ?show=dette,deficit,chomage (filtrer les KPIs, défaut: tous)
 *   ?counters=1 (afficher les compteurs temps réel)
 *   ?branding=1|0 (afficher le logo IFRAP-AI, défaut: 1)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const KPI_LABELS = {
  dette_etat: { label: 'Dette publique', icon: '📊' },
  charge_dette: { label: 'Charge de la dette', icon: '💰' },
  deficit_public: { label: 'Déficit budgétaire', icon: '📉' },
  depenses_publiques: { label: 'Dépenses publiques', icon: '🏛️' },
  prelevements: { label: 'Prélèvements obligatoires', icon: '🧾' },
  pib: { label: 'PIB France', icon: '📈' },
  croissance_pib: { label: 'Croissance PIB', icon: '📈' },
  inflation: { label: 'Inflation', icon: '🔥' },
  chomage: { label: 'Chômage', icon: '👥' },
  emploi_prive: { label: 'Emploi privé', icon: '🏢' },
  defaillances: { label: 'Défaillances entreprises', icon: '⚠️' },
  balance_commerciale: { label: 'Balance commerciale', icon: '🚢' },
  prix_electricite: { label: 'Prix électricité', icon: '⚡' },
  prod_nucleaire: { label: 'Production nucléaire', icon: '☢️' },
};

const COUNTER_CONFIG = {
  depenses: { label: 'Dépenses publiques', annual: 1_740_000_000_000, color: '#000091' },
  dette: { label: 'Endettement net', annual: 154_000_000_000, color: '#E1000F' },
  commerce: { label: 'Déficit commercial', annual: 69_200_000_000, color: '#D97706' },
};

const formatNumber = (n) => {
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(1) + ' Mds€';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + ' M€';
  if (Math.abs(n) >= 1e3) return Math.round(n).toLocaleString('fr-FR') + ' €';
  return n.toLocaleString('fr-FR');
};

const LiveCounter = ({ config, dark }) => {
  const [value, setValue] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const perMs = config.annual / (365.25 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const baseSinceMidnight = (now.getTime() - midnight) * perMs;

    const interval = setInterval(() => {
      const elapsed = Date.now() - midnight;
      setValue(elapsed * perMs);
    }, 50);
    return () => clearInterval(interval);
  }, [config.annual]);

  return (
    <div style={{
      background: dark ? '#0f0f20' : '#f8f9fa',
      borderLeft: `4px solid ${config.color}`,
      borderRadius: 8,
      padding: '12px 16px',
    }}>
      <div style={{ fontSize: 11, color: dark ? '#999' : '#666', marginBottom: 4 }}>{config.label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: config.color, fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(value)}
      </div>
      <div style={{ fontSize: 10, color: dark ? '#555' : '#aaa', marginTop: 2 }}>depuis minuit</div>
    </div>
  );
};

const EmbedKpisPage = () => {
  const [searchParams] = useSearchParams();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');

  const dark = searchParams.get('theme') === 'dark';
  const cols = parseInt(searchParams.get('cols') || '2', 10);
  const showFilter = searchParams.get('show')?.split(',') || [];
  const showCounters = searchParams.get('counters') === '1';
  const showBranding = searchParams.get('branding') !== '0';

  useEffect(() => {
    const fetchKpis = async () => {
      try {
        const res = await fetch(`${API_URL}/api/kpis/dashboard`);
        const data = await res.json();
        const allKpis = [];
        for (const cat of Object.values(data.categories || {})) {
          for (const kpi of cat.kpis || []) {
            if (!kpi.is_mock) allKpis.push(kpi);
          }
        }
        setKpis(allKpis);
        setLastUpdate(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      } catch (e) {
        console.error('Fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
    const interval = setInterval(fetchKpis, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const filteredKpis = showFilter.length > 0
    ? kpis.filter(k => showFilter.includes(k.code))
    : kpis;

  const bg = dark ? '#0a0a1a' : '#ffffff';
  const text = dark ? '#e5e5e5' : '#1a1a2e';
  const sub = dark ? '#888' : '#666';
  const border = dark ? '#1a1a2e' : '#eee';

  return (
    <div style={{
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      background: bg,
      color: text,
      padding: 16,
      minHeight: '100vh',
    }}>
      {/* Header */}
      {showBranding && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#000091' }}>IFRAP-AI</div>
            <div style={{ fontSize: 11, color: sub }}>Observatoire économique</div>
          </div>
          <div style={{ fontSize: 10, color: sub }}>
            Mis à jour à {lastUpdate}
          </div>
        </div>
      )}

      {/* Live Counters */}
      {showCounters && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(cols, 3)}, 1fr)`,
          gap: 10,
          marginBottom: 16,
        }}>
          {Object.entries(COUNTER_CONFIG).map(([key, config]) => (
            <LiveCounter key={key} config={config} dark={dark} />
          ))}
        </div>
      )}

      {/* KPIs Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: sub, fontSize: 13 }}>
          Chargement des données...
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 10,
        }}>
          {filteredKpis.map(kpi => {
            const meta = KPI_LABELS[kpi.code] || { label: kpi.code, icon: '📊' };
            const isNeg = parseFloat(kpi.value) < 0;
            const isCritical = kpi.criticality === 'critical';
            const valueColor = isCritical ? '#E1000F' : isNeg ? '#E1000F' : '#000091';

            return (
              <div key={kpi.code} style={{
                background: dark ? '#0f0f20' : '#f8f9fa',
                border: `1px solid ${border}`,
                borderRadius: 8,
                padding: '12px 14px',
                borderLeft: `3px solid ${valueColor}`,
              }}>
                <div style={{ fontSize: 11, color: sub, marginBottom: 4 }}>{meta.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: valueColor }}>
                    {typeof kpi.value === 'number' ? kpi.value.toLocaleString('fr-FR') : kpi.value}
                  </span>
                  <span style={{ fontSize: 11, color: sub }}>{kpi.unit}</span>
                </div>
                <div style={{ fontSize: 9, color: dark ? '#555' : '#bbb', marginTop: 4 }}>
                  {kpi.period} · {(kpi.source || '').substring(0, 30)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {showBranding && (
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 10, color: sub }}>
          <a
            href="https://www.ifrapai.org"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#000091', textDecoration: 'none' }}
          >
            ifrapai.org
          </a>
          {' '}· Fondation IFRAP · Données mises à jour en temps réel
        </div>
      )}
    </div>
  );
};

export default EmbedKpisPage;
